/**
 * Iter XX.197 — RevenueCat client wrapper for native Gully Plus IAP.
 *
 * PURPOSE
 * -------
 * Single-file public API used by `beginPlusPurchase.js`, the paywall
 * surfaces, and the "Restore purchases" button. Everything is a
 * no-op on WEB — Stripe checkout is still the ONLY web path.
 *
 * SECURITY / KEY HANDLING
 * -----------------------
 * The platform public SDK keys are read from
 * `REACT_APP_REVENUECAT_ANDROID_KEY` / `REACT_APP_REVENUECAT_IOS_KEY`
 * at build time. A missing key is a DETERMINISTIC build defect, not a
 * transient: `configure()` settles the purchase-availability state to
 * 'unavailable' with reason 'rc_key_missing', reports
 * `revenuecat_configure_result` once per session, and native purchase
 * CTAs render a truthful update-app state instead of a dead button
 * (see `NativePurchaseCta.jsx`). The production pipeline fails closed
 * on a missing key so a key-less build can never ship again.
 *
 * AVAILABILITY STATE (Iter XX.295)
 * --------------------------------
 * `getNativePurchaseAvailability()` → { state, reason }:
 *   'initializing' | 'ready' | 'unavailable' | 'not_native'.
 * `onPurchaseAvailabilityChanged(cb)` subscribes; `isRevenueCatReady()`
 * remains the synchronous gate used by beginPlusPurchase at tap time.
 *
 * NO SECRETS ARE HARDCODED. Public keys only.
 */

import { isNative, isAndroid, isIOS } from "./nativePlatform";
import { getBuildInfo } from "./nativeAppInfo";

// Iter XX.220 — Static import replaces the earlier lazy dynamic-import
// pattern that lived inside `_loadSdk`. Dynamic imports produce a
// separate webpack chunk that on iOS WKWebView loads AFTER Capacitor
// has finished plugin registration — resulting in the runtime
// "'Purchases' plugin is not implemented on ios" error. Static import
// guarantees the RevenueCat JS wrapper executes
// `Capacitor.registerPlugin('Purchases', ...)` during app-boot module
// eval, so the JS-side proxy is present before any code path calls
// `Purchases.configure()`. Web builds still parse this import but the
// module is a thin JS wrapper — no native side effects fire on web.
import { Purchases, LOG_LEVEL } from "@revenuecat/purchases-capacitor";

let _configured = false;
let _configuring = false;
let _lastCustomerInfo = null;
let _lastCustomerInfoAt = 0;

// Iter XX.295 — Purchase availability state machine (commercial P0).
//
// Every native CTA must know, BEFORE the player taps, whether this
// build can actually open StoreKit / Play Billing. `configureRevenueCat`
// settles this exactly once per session:
//
//   state 'initializing' — configure not yet attempted / in flight.
//   state 'ready'        — SDK configured; purchase path is live.
//   state 'unavailable'  — deterministic: this build can NEVER purchase
//                          (key missing, SDK unavailable, configure
//                          error). The UI must show a truthful
//                          update-app state, never a dead CTA.
//   state 'not_native'   — web. Stripe is the purchase path; this module
//                          is inert.
//
// `reason` is the precise configure reason ('rc_key_missing',
// 'rc_sdk_unavailable', 'rc_configure_error', 'rc_configuring',
// 'rc_ready', 'rc_not_native'). `getNativePurchaseAvailability`
// returns a STABLE object reference (only replaced on change) so React
// bindings can rely on referential equality.
// XX.295 corrected the pre-existing header claim that "CTAs stay
// hidden" until configure — that was only ever true for the Restore
// button. Purchase CTAs now read this state via NativePurchaseCta.
let _availabilitySnapshot = { state: "initializing", reason: "rc_configuring" };
const _availabilityListeners = new Set();
let _configureResultReported = false;

function _setAvailability(state, reason) {
  _availabilitySnapshot = { state, reason: reason || null };
  for (const cb of _availabilityListeners) {
    try {
      cb(_availabilitySnapshot);
    } catch {
      /* isolate */
    }
  }
}

/**
 * Current native purchase availability. Stable reference; replaced only
 * when the state actually changes.
 */
export function getNativePurchaseAvailability() {
  return _availabilitySnapshot;
}

/**
 * Subscribe to availability changes. Listener receives the new
 * { state, reason } snapshot. Returns an unsubscribe function.
 */
export function onPurchaseAvailabilityChanged(cb) {
  if (typeof cb !== "function") return () => {};
  _availabilityListeners.add(cb);
  return () => {
    _availabilityListeners.delete(cb);
  };
}

/** Precise reason from the last configure attempt. */
export function getConfigureReason() {
  return _availabilitySnapshot.reason;
}

// Iter XX.230 — Last-known-good monthly package cache (iOS purchase
// path resilience). RC Capacitor v11 on iOS can return an empty /
// partial `getOfferings()` payload on subsequent calls back-to-back
// with a user gesture. Call #1 (Plus page mount) fetches CA$12.99
// successfully; call #2 (purchase tap) returns null → `no_offering`
// → sheet never opens. This module-scoped fallback preserves the
// package resolved on the first successful fetch of the session so
// `purchasePackage` can still be reached. StoreKit re-resolves the
// SKProduct by identifier on the native side, so a slightly-older
// JS proxy is safe. Cache is single-session, in-memory only, and
// only populated by a real successful getOfferings() — never
// manufactured.
let _lastGoodMonthlyPackage = null;
const _listeners = new Set();

/** True on any platform where RC should be initialised.
 *
 *  Iter XX.206 — iOS unlocked. Both Android and iOS are eligible.
 *  Web is never eligible (Stripe checkout is the only web path).
 */
function _isEligiblePlatform() {
  return isNative();
}

/** Iter XX.206 — Pick the correct public SDK key for the current
 *  platform. Android continues to read `REACT_APP_REVENUECAT_ANDROID_KEY`
 *  exactly as before; iOS reads `REACT_APP_REVENUECAT_IOS_KEY`. Missing
 *  key → same graceful `rc_key_missing` fallback as today. Never
 *  cross-mixes keys.
 */
function _pickPlatformKey() {
  if (isAndroid()) return process.env.REACT_APP_REVENUECAT_ANDROID_KEY || "";
  if (isIOS())     return process.env.REACT_APP_REVENUECAT_IOS_KEY     || "";
  return "";
}

async function _loadSdk() {
  // Iter XX.220 — Kept as a thin async helper for call-site parity with
  // pre-XX.220 code. Returns the statically-imported SDK handles when
  // both bindings are truthy; otherwise returns null so callers can
  // no-op gracefully (e.g. tree-shaken web build where the module was
  // stripped or a non-native platform where `Purchases` is undefined).
  if (Purchases && LOG_LEVEL) {
    return { Purchases, LOG_LEVEL };
  }
  return null;
}

/**
 * Configure the RevenueCat SDK. Idempotent — safe to call from every
 * bootstrap path.
 *
 * Iter XX.206 — Picks the platform-specific public key:
 *   · Android → REACT_APP_REVENUECAT_ANDROID_KEY (unchanged)
 *   · iOS     → REACT_APP_REVENUECAT_IOS_KEY
 * Missing key → returns { ok: false, reason: 'rc_key_missing' } as
 * before. `appUserID` is the Gully user_id when the user is signed in
 * (optional — anonymous purchases are tracked against an RC-generated
 * anonymous id and merged into the real user via `logIn()` post-auth).
 */
export async function configureRevenueCat(appUserID) {
  if (_configured) {
    _setAvailability("ready", "rc_ready");
    return { ok: true, alreadyConfigured: true };
  }
  if (_configuring) return { ok: false, reason: "rc_configuring" };
  if (!_isEligiblePlatform()) {
    // Web — Stripe is the purchase path; RC stays inert.
    _setAvailability("not_native", "rc_not_native");
    return { ok: false, reason: "rc_not_native" };
  }
  const key = _pickPlatformKey();
  if (!key || !String(key).trim()) {
    // Iter XX.295 — Deterministic build defect, not a transient.
    // The production pipeline fails closed on a missing key
    // (deploy-frontend.yml), so reaching here means a binary was built
    // outside the pipeline or from a stale bundle. Mark the purchase
    // path unavailable and report it — the UI must show a truthful
    // update-app state, never a dead purchase CTA.
    _setAvailability("unavailable", "rc_key_missing");
    _reportConfigureResult();
    return { ok: false, reason: "rc_key_missing" };
  }
  const sdk = await _loadSdk();
  if (!sdk || !sdk.Purchases) {
    _setAvailability("unavailable", "rc_sdk_unavailable");
    _reportConfigureResult();
    return { ok: false, reason: "rc_sdk_unavailable" };
  }

  _configuring = true;
  try {
    // Silence chatty RC logs in prod; keep DEBUG on non-prod builds.
    if (process.env.NODE_ENV !== "production" && sdk.LOG_LEVEL) {
      await sdk.Purchases.setLogLevel({ level: sdk.LOG_LEVEL.WARN });
    }
    await sdk.Purchases.configure({
      apiKey:    String(key).trim(),
      ...(appUserID ? { appUserID: String(appUserID) } : {}),
    });
    _configured = true;
    _setAvailability("ready", "rc_ready");
    _reportConfigureResult();
    // Wire the single global listener; component-level listeners
    // simply add to `_listeners` and receive fanned-out updates.
    try {
      await sdk.Purchases.addCustomerInfoUpdateListener((info) => {
        _lastCustomerInfo = info;
        _lastCustomerInfoAt = Date.now();
        for (const cb of _listeners) {
          try { cb(info); } catch { /* isolate */ }
        }
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[revenueCat] listener wire failed:", e?.message || e);
    }
    return { ok: true, configured: true };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[revenueCat] configure failed:", e?.message || e);
    _setAvailability("unavailable", "rc_configure_error");
    _reportConfigureResult();
    return { ok: false, reason: "rc_configure_error", error: String(e) };
  } finally {
    _configuring = false;
  }
}

/**
 * Iter XX.295 — Report the configure outcome once per session to the
 * existing purchase-diagnostics endpoint (`phase: rc_configure_result`).
 *
 * This is the production tripwire: ANY `rc_key_missing` from a native
 * production build means the pipeline's fail-closed validation was
 * bypassed and the release is a commercial P0. Carries the precise
 * reason plus the exact binary identity (build_id, native app
 * version/build) so the alert can name the offending build.
 *
 * Native only — web configure is intentionally inert and unreported.
 * Fire-and-forget; never affects the purchase path.
 */
async function _reportConfigureResult() {
  if (_configureResultReported) return;
  _configureResultReported = true;
  try {
    if (!_isEligiblePlatform()) return;
    const snap = getNativePurchaseAvailability();
    const build = await getBuildInfo(2000);
    await _postPurchaseDiag({
      phase: "rc_configure_result",
      configure_state: snap.state,
      configure_reason: snap.reason,
      build_id: build.build_id,
      native_app_version: build.app_version,
      native_app_build: build.app_build,
    });
  } catch {
    /* fire-and-forget */
  }
}

/**
 * Attach the RC anonymous customer to a real Gully user_id after
 * sign-in. Safe to call more than once; RC handles the alias merge.
 */
export async function logInRevenueCat(appUserID) {
  if (!_configured || !appUserID) return { ok: false, reason: "rc_not_ready" };
  const sdk = await _loadSdk();
  try {
    const res = await sdk.Purchases.logIn({ appUserID: String(appUserID) });
    _lastCustomerInfo   = res?.customerInfo || _lastCustomerInfo;
    _lastCustomerInfoAt = Date.now();
    return { ok: true, customerInfo: res?.customerInfo, created: res?.created };
  } catch (e) {
    return { ok: false, reason: "rc_login_error", error: String(e) };
  }
}

/** Whether the SDK is configured AND we're on Android native. */
export function isRevenueCatReady() {
  return _configured && _isEligiblePlatform();
}

/**
 * Fetch the current offering + its monthly package. Returns null on
 * any error so callers can hide the CTA gracefully.
 */
export async function getMonthlyPackage() {
  if (!isRevenueCatReady()) {
    // Iter XX.231 — TEMPORARY diagnostic: prove the early-return
    // branch when RC is not ready. Also surfaces whether the cached
    // package is being returned instead of a fresh fetch.
    // eslint-disable-next-line no-console
    console.log("[gully-purchase-diag] getMonthlyPackage early-return: rc_not_ready", {
      _configured,
      eligible_platform: _isEligiblePlatform(),
      returning_cached: !!_lastGoodMonthlyPackage,
      cached_identifier: _lastGoodMonthlyPackage?.identifier ?? null,
    });
    return _lastGoodMonthlyPackage;
  }
  const sdk = await _loadSdk();
  try {
    const res = await sdk.Purchases.getOfferings();
    // Iter XX.231 — TEMPORARY diagnostic: dump the RAW getOfferings
    // response shape so we can see EXACTLY what RC hands back on iOS.
    // This is the single most important data point for the P0 issue.
    try {
      const _current = res?.current || res?.offerings?.current;
      const _availableCount = Array.isArray(_current?.availablePackages)
        ? _current.availablePackages.length
        : null;
      const _firstPkg = Array.isArray(_current?.availablePackages)
        ? _current.availablePackages[0]
        : null;
      // eslint-disable-next-line no-console
      console.log("[gully-purchase-diag] getOfferings raw", {
        res_top_keys:            (() => { try { return Object.keys(res || {}); } catch { return "err"; } })(),
        res_has_current:         !!res?.current,
        res_has_offerings_curr:  !!res?.offerings?.current,
        current_identifier:      _current?.identifier ?? null,
        current_has_monthly:     !!_current?.monthly,
        monthly_identifier:      _current?.monthly?.identifier ?? null,
        monthly_product_id:      _current?.monthly?.product?.identifier ?? null,
        monthly_price_string:    _current?.monthly?.product?.priceString ?? null,
        available_pkg_count:     _availableCount,
        first_pkg_identifier:    _firstPkg?.identifier ?? null,
        first_pkg_product_id:    _firstPkg?.product?.identifier ?? null,
        first_pkg_price_string:  _firstPkg?.product?.priceString ?? null,
        offerings_all_keys:      (() => { try { return Object.keys(res?.offerings?.all || {}); } catch { return "err"; } })(),
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log("[gully-purchase-diag] getOfferings raw dump failed", {
        error: String((e && e.message) || e),
      });
    }
    // Per founder decision (Feb 2026) — a single "default" offering
    // with a single monthly plan. RC surfaces this as `.current`.
    const current = res?.current || res?.offerings?.current;
    // Prefer the `monthly` package shortcut; fall back to the first
    // available package if `monthly` isn't wired yet in the RC dashboard.
    const monthly = current
      ? (current.monthly || (current.availablePackages || [])[0] || null)
      : null;
    if (monthly) {
      // Iter XX.230 — remember the last successful resolution so a
      // subsequent flaky call can fall back to it (see cache doc
      // near the top of this file).
      _lastGoodMonthlyPackage = monthly;
      // Iter XX.231 — TEMPORARY diagnostic: branch = fresh success.
      // eslint-disable-next-line no-console
      console.log("[gully-purchase-diag] getMonthlyPackage branch: fresh_success", {
        identifier:       monthly?.identifier ?? null,
        product_id:       monthly?.product?.identifier ?? null,
        price_string:     monthly?.product?.priceString ?? null,
        currency_code:    monthly?.product?.currencyCode ?? null,
      });
      return monthly;
    }
    // Fresh fetch produced nothing usable — reuse the last valid pkg
    // resolved earlier in this session (identifier is still the same
    // product Apple approved). Never manufactured: `null` until a
    // real fetch succeeded at least once.
    // Iter XX.231 — TEMPORARY diagnostic: branch = fresh empty →
    // cache fallback (or final null if cache never populated).
    // eslint-disable-next-line no-console
    console.log("[gully-purchase-diag] getMonthlyPackage branch: fresh_empty_falling_back", {
      cache_populated:  !!_lastGoodMonthlyPackage,
      cached_identifier: _lastGoodMonthlyPackage?.identifier ?? null,
      cached_product_id: _lastGoodMonthlyPackage?.product?.identifier ?? null,
    });
    return _lastGoodMonthlyPackage;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[revenueCat] getOfferings failed:", e?.message || e);
    // Iter XX.231 — TEMPORARY diagnostic: branch = catch → cache fallback.
    // eslint-disable-next-line no-console
    console.log("[gully-purchase-diag] getMonthlyPackage branch: catch_falling_back", {
      error:             String((e && e.message) || e),
      cache_populated:   !!_lastGoodMonthlyPackage,
      cached_identifier: _lastGoodMonthlyPackage?.identifier ?? null,
      cached_product_id: _lastGoodMonthlyPackage?.product?.identifier ?? null,
    });
    // XX.240c — Persist the RC getOfferings() failure to the backend
    // diagnostics so we can finally see WHAT RC is throwing. This is
    // the missing signal: the console shows `catch_falling_back` but
    // the actual RC / StoreKit error text is only in console.warn
    // above (which the founder cannot inspect on production TestFlight).
    await _postPurchaseDiag({
      phase:                 "get_offerings_threw",
      readable_error_code:   e?.readableErrorCode ?? "GET_OFFERINGS_THREW",
      underlying_error_code: e?.underlyingErrorCode != null ? String(e.underlyingErrorCode) : (e?.underlyingErrorMessage ?? null),
      error_message:         String((e && e.message) || e),
      error_domain:          e?.domain ?? null,
      error_keys:            _errorKeys(e),
      extra: {
        errorCode:            e?.errorCode ?? null,
        code:                 e?.code ?? null,
        localizedDescription: e?.localizedDescription ?? null,
        underlyingErrorMessage: e?.underlyingErrorMessage ?? null,
        name:                 (e && e.name) || null,
        cache_populated:      !!_lastGoodMonthlyPackage,
      },
    });
    return _lastGoodMonthlyPackage;
  }
}

/**
 * Iter XX.267c — StoreKit / Play Billing DIRECT product fetch.
 *
 * When `getMonthlyPackage()` returns null because the RevenueCat
 * dashboard offering isn't wired (or `getOfferings()` transiently
 * returns an empty `current`), StoreKit itself may STILL know about
 * the product — the founder's Xcode logs confirmed this on iPhone:
 *
 *   STOREKIT_DIRECT_COUNT=1
 *   STOREKIT_DIRECT_ID=plus_monthly_ios
 *   STOREKIT_DIRECT_PRICE=$12.99
 *
 * This function calls `Purchases.getProducts({productIdentifiers})`
 * which hits StoreKit / Play Billing directly and returns the
 * localized product regardless of offering configuration. The
 * returned shape is a `PurchasesStoreProduct` (identifier +
 * priceString + currencyCode). We DO NOT purchase against this
 * result — purchase is still restricted to a resolved
 * `PurchasesPackage` so entitlement plumbing stays untouched.
 *
 * Purely a DISPLAY fallback so the paywall stops rendering
 * "Loading price…" when RC returns an empty offering.
 *
 * @param {string[]} productIds — product identifiers to fetch
 * @returns {Promise<{identifier: string, priceString: string, currencyCode: string} | null>}
 */
const PRO_PRICE_DIAG_PREFIX = "[XX.267c-price]";
export async function getStoreProductDirect(productIds = ["plus_monthly_ios"]) {
  if (!isRevenueCatReady()) {
    try {
      // eslint-disable-next-line no-console
      console.log(PRO_PRICE_DIAG_PREFIX + " getStoreProductDirect early-return: rc_not_ready", {
        _configured, eligible_platform: _isEligiblePlatform(),
      });
    } catch { /* noop */ }
    return null;
  }
  const sdk = await _loadSdk();
  // Iter XX.267d — restore known-working behavior. On the first
  // real-device call StoreKit itself may still be warming up
  // (offering configuration lands via a background fetch); calling
  // `getProducts` too early returns `products=[]` even though the
  // product is fully valid. Retry with a short bounded backoff so a
  // valid native price ALWAYS reaches the JS layer once StoreKit has
  // resolved it. Purchase / entitlement / product IDs / offerings /
  // regional pricing remain untouched — this is a display-side
  // resilience upgrade.
  const RETRY_DELAYS_MS = [0, 350, 700, 1200, 2000]; // ~4.25s worst case
  for (let attempt = 0; attempt < RETRY_DELAYS_MS.length; attempt++) {
    const delay = RETRY_DELAYS_MS[attempt];
    if (delay > 0) {
      await new Promise((r) => setTimeout(r, delay));
    }
    try {
      const res = await sdk.Purchases.getProducts({ productIdentifiers: productIds });
      const products = Array.isArray(res?.products) ? res.products : [];
      try {
        // eslint-disable-next-line no-console
        console.log(PRO_PRICE_DIAG_PREFIX + " getStoreProductDirect attempt", {
          attempt,
          requested: productIds,
          count: products.length,
          first_id: products[0]?.identifier ?? null,
          first_price_string: products[0]?.priceString ?? null,
          first_currency: products[0]?.currencyCode ?? null,
        });
      } catch { /* noop */ }
      if (products.length > 0) {
        const p = products[0];
        return {
          identifier:   p?.identifier || null,
          priceString:  p?.priceString || null,
          currencyCode: p?.currencyCode || null,
        };
      }
      // Empty on this attempt — fall through to next retry.
    } catch (e) {
      try {
        // eslint-disable-next-line no-console
        console.log(PRO_PRICE_DIAG_PREFIX + " getStoreProductDirect attempt threw", {
          attempt,
          error: String(e?.message || e),
        });
      } catch { /* noop */ }
      // Continue retry loop — do NOT bail on a single failure.
    }
  }
  try {
    // eslint-disable-next-line no-console
    console.log(PRO_PRICE_DIAG_PREFIX + " getStoreProductDirect exhausted all retries", {
      requested: productIds,
    });
  } catch { /* noop */ }
  return null;
}

/**
 * Iter XX.267d — Restore known-working plus-price display path.
 * Iter XX.269 — Split price resolution from RC-readiness gate.
 *
 * Previously we bailed at `rc_not_ready_after_wait` whenever
 * `configureRevenueCat()` had not completed by the poll deadline —
 * even though StoreKit itself may still be fully operational (Xcode
 * `STOREKIT_DIRECT_PRICE=$12.99` proves the native product resolves
 * independently of the RC configure handshake). We now try to force
 * configure ONCE, then attempt fetches regardless of RC readiness;
 * the underlying `Purchases` binding surfaces the localized price as
 * soon as it is available. Purchase / entitlement / product IDs and
 * regional pricing are untouched — this is a display-side unblock.
 *
 * Single entry point the UI can call once from a mount effect. Walks
 * the canonical resolution ladder and returns the FIRST priceString
 * it gets:
 *
 *   1. `_lastGoodMonthlyPackage` cache — instant on repeat mounts
 *   2. `getMonthlyPackage()` — RC offering / dashboard-configured
 *   3. `getStoreProductDirect([productId])` — direct StoreKit /
 *      Play Billing lookup by product identifier
 *
 * The moment ANY step returns a valid priceString the caller can
 * flip `priceLoaded=true`.
 *
 * @returns {Promise<{priceString: string, currencyCode: string|null,
 *                    source: "cache"|"offering"|"storekit_direct"} | null>}
 */
const _XX269_PRICE = "[XX.269-price]";
export async function resolveDisplayPrice(productId = "plus_monthly_ios",
                                          { maxWaitMs = 10000, pollMs = 200 } = {}) {
  // 1) Cache path — always fastest.
  if (_lastGoodMonthlyPackage?.product?.priceString) {
    try {
      // eslint-disable-next-line no-console
      console.info(_XX269_PRICE + " cache_hit", {
        price_string:  _lastGoodMonthlyPackage.product.priceString,
        currency_code: _lastGoodMonthlyPackage.product.currencyCode || null,
      });
    } catch { /* noop */ }
    return {
      priceString:  _lastGoodMonthlyPackage.product.priceString,
      currencyCode: _lastGoodMonthlyPackage.product.currencyCode || null,
      source:       "cache",
    };
  }
  // 2) Iter XX.269 — Force a configure attempt ONCE. Idempotent:
  // returns immediately when already configured. The reason we
  // don't wait for the boot-time configure is that on some builds
  // `configureRevenueCat()` runs before RC's underlying binding is
  // fully ready, hits `rc_configuring` / `rc_sdk_unavailable`, and
  // never gets retried anywhere. Doing it here guarantees the
  // resolver never stalls waiting on a boot call that already gave
  // up.
  try {
    const r = await configureRevenueCat();
    // eslint-disable-next-line no-console
    console.info(_XX269_PRICE + " configure_attempt", {
      ok:               !!(r && r.ok),
      already:          !!(r && r.alreadyConfigured),
      reason:           r?.reason || null,
      rc_ready_after:   isRevenueCatReady(),
    });
  } catch (e) {
    try {
      // eslint-disable-next-line no-console
      console.info(_XX269_PRICE + " configure_threw",
        { error: String(e?.message || e) });
    } catch { /* noop */ }
  }
  // 3) Bounded poll — give RC up to `maxWaitMs` to become ready in
  // case configure() is still in flight from bootstrap. This is
  // best-effort; if RC never flips to ready, we STILL attempt the
  // fetches below because the underlying binding can respond.
  const startedAt = Date.now();
  while (!isRevenueCatReady() && Date.now() - startedAt < maxWaitMs) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, pollMs));
  }
  const rcReady = isRevenueCatReady();
  try {
    // eslint-disable-next-line no-console
    console.info(_XX269_PRICE + " after_wait", {
      rc_ready:         rcReady,
      waited_ms:        Date.now() - startedAt,
      eligible_platform: _isEligiblePlatform(),
    });
  } catch { /* noop */ }
  // 4) Try offering path — canonical route when RC dashboard is
  // wired. Skips silently on failure.
  try {
    const pkg = await getMonthlyPackage();
    if (pkg?.product?.priceString) {
      try {
        // eslint-disable-next-line no-console
        console.info(_XX269_PRICE + " offering_hit", {
          price_string:  pkg.product.priceString,
          currency_code: pkg.product.currencyCode || null,
        });
      } catch { /* noop */ }
      return {
        priceString:  pkg.product.priceString,
        currencyCode: pkg.product.currencyCode || null,
        source:       "offering",
      };
    }
  } catch (e) {
    try {
      // eslint-disable-next-line no-console
      console.info(_XX269_PRICE + " offering_threw",
        { error: String(e?.message || e) });
    } catch { /* noop */ }
  }
  // 5) StoreKit-direct fallback. Iter XX.269 — we DO NOT gate this
  // on `isRevenueCatReady()` anymore. The underlying `Purchases`
  // binding can resolve products even if RC's own `configure()`
  // handshake is incomplete on this build. If the binding is
  // genuinely absent the internal call fails cleanly and we return
  // null; the paywall then falls back to its Stripe display.
  try {
    const direct = await _getStoreProductDirectUnsafe([productId]);
    if (direct?.priceString) {
      try {
        // eslint-disable-next-line no-console
        console.info(_XX269_PRICE + " storekit_direct_hit", {
          product_id:    direct.identifier,
          price_string:  direct.priceString,
          currency_code: direct.currencyCode || null,
          rc_ready:      rcReady,
        });
      } catch { /* noop */ }
      return {
        priceString:  direct.priceString,
        currencyCode: direct.currencyCode || null,
        source:       "storekit_direct",
      };
    }
  } catch (e) {
    try {
      // eslint-disable-next-line no-console
      console.info(_XX269_PRICE + " storekit_direct_threw",
        { error: String(e?.message || e) });
    } catch { /* noop */ }
  }
  try {
    // eslint-disable-next-line no-console
    console.info(_XX269_PRICE + " resolveDisplayPrice returning_null",
      { requested: productId, rc_ready: rcReady });
  } catch { /* noop */ }
  return null;
}

/**
 * Iter XX.269 — Unfettered StoreKit-direct fetch.
 *
 * Identical retry ladder to `getStoreProductDirect` but WITHOUT the
 * `isRevenueCatReady()` early-return. Same purpose: recover from
 * builds where `configure()` didn't complete but StoreKit itself is
 * ready. Purchase paths still use the RC-gated path — this is
 * DISPLAY-ONLY.
 */
async function _getStoreProductDirectUnsafe(productIds = ["plus_monthly_ios"]) {
  const sdk = await _loadSdk();
  if (!sdk?.Purchases?.getProducts) {
    try {
      // eslint-disable-next-line no-console
      console.info(_XX269_PRICE + " storekit_direct sdk_unavailable");
    } catch { /* noop */ }
    return null;
  }
  const RETRY_DELAYS_MS = [0, 350, 700, 1200, 2000];
  for (let attempt = 0; attempt < RETRY_DELAYS_MS.length; attempt++) {
    const delay = RETRY_DELAYS_MS[attempt];
    if (delay > 0) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, delay));
    }
    try {
      // eslint-disable-next-line no-await-in-loop
      const res = await sdk.Purchases.getProducts({ productIdentifiers: productIds });
      const products = Array.isArray(res?.products) ? res.products : [];
      try {
        // eslint-disable-next-line no-console
        console.info(_XX269_PRICE + " storekit_direct attempt", {
          attempt, count: products.length,
          first_id: products[0]?.identifier ?? null,
          first_price_string: products[0]?.priceString ?? null,
        });
      } catch { /* noop */ }
      if (products.length > 0) {
        const p = products[0];
        return {
          identifier:   p?.identifier || null,
          priceString:  p?.priceString || null,
          currencyCode: p?.currencyCode || null,
        };
      }
    } catch (e) {
      try {
        // eslint-disable-next-line no-console
        console.info(_XX269_PRICE + " storekit_direct attempt_threw",
          { attempt, error: String(e?.message || e) });
      } catch { /* noop */ }
    }
  }
  return null;
}

/**
 * Kick off the native purchase sheet. Resolves with a normalised
 * result:
 *   { ok: true, plusActive: bool, customerInfo }
 *   { ok: false, reason: 'user_cancelled' | 'pending' | 'error', error? }
 */

// Iter XX.240 — Persistent server-side purchase diagnostics.
// Every phase of the purchase attempt (`rc_not_ready`, `no_offering`,
// `about_to_purchase`, `resolved`, `error`, `user_cancelled`, `pending`)
// posts a small payload to the backend so the founder can inspect the
// real StoreKit / RevenueCat outcome after reproducing on a physical
// iPhone (Xcode + Safari consoles both stayed empty during the tap,
// so we cannot rely on client-only logs). Diagnostics-only — never
// affects the purchase behaviour.
let _diagAttemptId = null;

function _newAttemptId() {
  try {
    if (crypto && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  } catch { /* noop */ }
  return "att_" + Date.now() + "_" + Math.random().toString(36).slice(2, 10);
}

async function _postPurchaseDiag(payload) {
  try {
    // XX.240a — read the base URL the same way every other production
    // call site in the app does (`api.js`, `performanceCapture.js`,
    // `liveBreakthroughs.js`, `progressiveReveal.js`, `connectCricket.js`).
    // The original `typeof process !== "undefined"` guard evaluated to
    // false in the CRA-compiled bundle at runtime, silently short-
    // circuiting the diagnostic before the fetch ever fired.
    const base = process.env.REACT_APP_BACKEND_URL || "";
    if (!base) return;
    let session_id = null;
    try { session_id = window.localStorage.getItem("gully_session_id"); } catch { /* noop */ }
    if (!session_id) {
      session_id = "sess_" + Date.now() + "_" + Math.random().toString(36).slice(2, 10);
      try { window.localStorage.setItem("gully_session_id", session_id); } catch { /* noop */ }
    }
    let capacitor_platform = null, app_build = null, ua = null;
    try { capacitor_platform = window.Capacitor && window.Capacitor.getPlatform && window.Capacitor.getPlatform(); } catch { /* noop */ }
    try { ua = window.navigator && window.navigator.userAgent; } catch { /* noop */ }
    try { app_build = window.__GULLY_APP_BUILD__ || null; } catch { /* noop */ }
    const enriched = {
      attempt_id:         _diagAttemptId,
      platform:           isIOS() ? "ios" : (isAndroid() ? "android" : "web"),
      capacitor_platform, app_build,
      user_agent:         ua,
      session_id,
      sdk_version:        "@revenuecat/purchases-capacitor@11.3.1",
      ...(payload || {}),
    };
    // Fire-and-forget. `keepalive: true` so the POST survives even if
    // the WebView blurs / backgrounds during a StoreKit sheet dismiss.
    await fetch(base + "/api/diagnostics/ios_purchase", {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify(enriched),
      keepalive: true,
    });
  } catch (_e) {
    // Never let a diagnostic post block the real purchase flow.
    try {
      // eslint-disable-next-line no-console
      console.log("[gully-purchase-diag] backend-post failed (non-fatal)",
        { error: String((_e && _e.message) || _e) });
    } catch { /* noop */ }
  }
}

function _errorKeys(e) {
  try { return Object.keys(e || {}); } catch { return null; }
}

// ---------------------------------------------------------------------------
// Iter XX.283 — RevenueCat / Play Billing / StoreKit error normalization.
//
// Purpose
// -------
// Currently `plus_purchase_failed_native` records `reason: "error"` for
// every RC-side failure that isn't a user cancel or pending purchase.
// That collapses billing_unavailable / product_unavailable / network /
// configuration / store-problem / already-owned into a single opaque
// bucket, blocking root-cause diagnosis of native purchase drops.
//
// This helper produces a stable, low-cardinality category tag from the
// three signals RC 11+ exposes on both platforms:
//   · `err.readableErrorCode`  — string form (preferred, cross-platform)
//   · `err.errorCode`          — numeric form (RC PurchasesErrorCode enum)
//   · `err.underlyingErrorMessage` / `err.message` — free-form fallback
//     used to disambiguate Play `BILLING_UNAVAILABLE` (wrapped by RC as
//     STORE_PROBLEM but semantically distinct) and to catch iOS
//     StoreKit "cannot connect to iTunes Store" surface strings.
//
// Categories (matches XX.283 spec):
//   · "user_cancelled"
//   · "product_unavailable"
//   · "billing_unavailable"
//   · "network"
//   · "configuration"
//   · "store_problem"
//   · "already_purchased_or_pending"
//   · "unknown"
//
// Behavior-preserving: this is pure instrumentation. The UI still shows
// the same generic "Couldn't complete that purchase." toast. Nothing
// about pricing, offer, or purchase behavior changes.
// ---------------------------------------------------------------------------
const _RC_READABLE_TO_CATEGORY = {
  // https://www.revenuecat.com/docs/errors
  PURCHASE_CANCELLED:                       "user_cancelled",
  PRODUCT_NOT_AVAILABLE_FOR_PURCHASE:       "product_unavailable",
  PRODUCT_REQUEST_TIMEOUT:                  "product_unavailable",
  STORE_PROBLEM:                            "store_problem",
  UNEXPECTED_BACKEND_RESPONSE_ERROR:        "store_problem",
  UNKNOWN_BACKEND_ERROR:                    "store_problem",
  NETWORK_ERROR:                            "network",
  OFFLINE_CONNECTION_ERROR:                 "network",
  API_ENDPOINT_BLOCKED_ERROR:               "network",
  PRODUCT_ALREADY_PURCHASED:                "already_purchased_or_pending",
  RECEIPT_ALREADY_IN_USE:                   "already_purchased_or_pending",
  RECEIPT_IN_USE_BY_OTHER_SUBSCRIBER:       "already_purchased_or_pending",
  OPERATION_ALREADY_IN_PROGRESS:            "already_purchased_or_pending",
  PAYMENT_PENDING:                          "already_purchased_or_pending",
  PURCHASE_NOT_ALLOWED:                     "configuration",
  PURCHASE_INVALID:                         "configuration",
  INVALID_RECEIPT:                          "configuration",
  MISSING_RECEIPT_FILE:                     "configuration",
  INVALID_CREDENTIALS:                      "configuration",
  INVALID_APP_USER_ID:                      "configuration",
  INVALID_SUBSCRIBER_ATTRIBUTES:            "configuration",
  INVALID_PROMOTIONAL_OFFER:                "configuration",
  INSUFFICIENT_PERMISSIONS:                 "configuration",
  CONFIGURATION_ERROR:                      "configuration",
  UNSUPPORTED_ERROR:                        "configuration",
  UNKNOWN:                                  "unknown",
};

// RC PurchasesErrorCode numeric map (belt-and-braces for platforms
// that don't populate `readableErrorCode`). Matches the JS SDK enum
// as of v11.
const _RC_NUMERIC_TO_CATEGORY = {
  0:  "unknown",
  1:  "user_cancelled",
  2:  "store_problem",
  3:  "configuration",
  4:  "configuration",
  5:  "product_unavailable",
  6:  "already_purchased_or_pending",
  7:  "already_purchased_or_pending",
  8:  "configuration",
  9:  "configuration",
  10: "network",
  11: "configuration",
  12: "store_problem",
  13: "already_purchased_or_pending",
  14: "configuration",
  15: "already_purchased_or_pending",
  16: "store_problem",
  19: "configuration",
  20: "already_purchased_or_pending",
  21: "configuration",
  23: "configuration",
  24: "configuration",
  32: "product_unavailable",
  33: "network",
  35: "network",
};

/**
 * Normalize a thrown RC / Play / StoreKit error into a stable
 * low-cardinality category. Also handles the two pre-throw early
 * returns from `purchasePlusMonthly` (`rc_not_ready`, `no_offering`)
 * when passed the string reason directly.
 *
 * Never throws. Never fetches. Returns "unknown" for anything it
 * can't classify — that itself becomes a useful signal in aggregate.
 */
export function _normalizeRcErrorCategory(errOrReason) {
  try {
    if (typeof errOrReason === "string") {
      if (errOrReason === "rc_not_ready")  return "configuration";
      if (errOrReason === "no_offering")   return "product_unavailable";
      if (errOrReason === "user_cancelled") return "user_cancelled";
      if (errOrReason === "pending")       return "already_purchased_or_pending";
      return "unknown";
    }
    const e = errOrReason || {};
    if (e.userCancelled === true) return "user_cancelled";
    const readable = typeof e.readableErrorCode === "string"
      ? e.readableErrorCode.toUpperCase()
      : null;
    // Special-case: Play "BILLING_UNAVAILABLE" is delivered as
    // STORE_PROBLEM at the RC layer but with the string
    // "BILLING_UNAVAILABLE" in the underlyingErrorMessage. Treat as
    // a distinct category so ops can spot "user needs to add a
    // payment method" separately from "Play Store is down".
    const under = typeof e.underlyingErrorMessage === "string"
      ? e.underlyingErrorMessage : "";
    const msg   = typeof e.message === "string" ? e.message : "";
    if (/BILLING_UNAVAILABLE/i.test(under)
        || /BILLING_UNAVAILABLE/i.test(msg)) {
      return "billing_unavailable";
    }
    // iOS StoreKit surface strings for offline / connect failures.
    if (/cannot connect to itunes store|the internet connection appears to be offline|nsurlerrordomain -100[09]/i
        .test(under + " " + msg)) {
      return "network";
    }
    if (readable && _RC_READABLE_TO_CATEGORY[readable]) {
      return _RC_READABLE_TO_CATEGORY[readable];
    }
    if (typeof e.errorCode === "number"
        && _RC_NUMERIC_TO_CATEGORY[e.errorCode]) {
      return _RC_NUMERIC_TO_CATEGORY[e.errorCode];
    }
  } catch { /* fall through */ }
  return "unknown";
}

export async function purchasePlusMonthly() {
  // Iter XX.240 — start a fresh attempt id for this tap.
  _diagAttemptId = _newAttemptId();
  _postPurchaseDiag({ phase: "started" });
  if (!isRevenueCatReady()) {
    // Iter XX.229 — TEMPORARY diagnostic.
    // eslint-disable-next-line no-console
    console.log("[gully-purchase-diag] purchasePlusMonthly early-return: rc_not_ready", {
      _configured,
      eligible_platform: _isEligiblePlatform(),
    });
    _postPurchaseDiag({
      phase: "error",
      readable_error_code: "RC_NOT_READY",
      error_message: "isRevenueCatReady()=false — SDK not configured on this platform",
    });
    // Iter XX.283 — Attach normalized error_category so downstream
    // funnel events can distinguish this from a StoreKit failure.
    return { ok: false, reason: "rc_not_ready",
             error_category: _normalizeRcErrorCategory("rc_not_ready") };
  }
  // XX.240b — Prefer the last-known-good pkg from paywall hydration
  // over a fresh getOfferings() refetch. Founder-observed failure
  // (Feb 2026): paywall rendered CA$12.99 successfully, tap then
  // produced NO_OFFERING because purchasePlusMonthly() re-invoked
  // getMonthlyPackage() → getOfferings(), which flaked on the
  // subsequent iOS call and returned an empty offering. The pkg
  // that already produced CA$12.99 is a valid StoreKit object; use
  // it verbatim. Only fall back to a fresh fetch if the cache was
  // never populated (first-ever call scenario).
  const pkg = _lastGoodMonthlyPackage || await getMonthlyPackage();
  if (!pkg) {
    // eslint-disable-next-line no-console
    console.log("[gully-purchase-diag] purchasePlusMonthly early-return: no_offering");
    // XX.240c — AWAIT the diagnostic post so WKWebView cannot drop it
    // when the async function returns immediately after this branch.
    // Previous fire-and-forget was inconsistent across iOS versions
    // even with keepalive: true.
    await _postPurchaseDiag({
      phase: "error",
      readable_error_code: "NO_OFFERING",
      error_message: "getMonthlyPackage() returned null — offering/package not resolved",
    });
    // Iter XX.283 — Attach normalized category. NO_OFFERING is a
    // product-availability failure (missing store product / offering
    // configured incorrectly / user's country not entitled).
    return { ok: false, reason: "no_offering",
             error_category: _normalizeRcErrorCategory("no_offering") };
  }
  const sdk = await _loadSdk();
  // Iter XX.229 — TEMPORARY diagnostic: confirm RC App User ID and
  // exact package/product identity JUST before the native purchase
  // sheet is triggered.
  try {
    let _rcUserID = null;
    try {
      const _r = await sdk.Purchases.getAppUserID();
      _rcUserID = (_r && (_r.appUserID ?? _r)) || null;
    } catch (e) {
      _rcUserID = "getAppUserID_error:" + String((e && e.message) || e);
    }
    // eslint-disable-next-line no-console
    console.log("[gully-purchase-diag] about to call Purchases.purchasePackage", {
      rc_app_user_id:        _rcUserID,
      configured:            _configured,
      package_identifier:    pkg?.identifier ?? null,
      package_offering_id:   pkg?.offeringIdentifier ?? null,
      product_identifier:    pkg?.product?.identifier ?? null,
      product_price_string:  pkg?.product?.priceString ?? null,
      product_currency_code: pkg?.product?.currencyCode ?? null,
    });
    _postPurchaseDiag({
      phase: "about_to_purchase",
      anonymous_id:         _rcUserID,
      package_identifier:   pkg?.identifier ?? null,
      offering_identifier:  pkg?.offeringIdentifier ?? null,
      product_identifier:   pkg?.product?.identifier ?? null,
      localized_price:      pkg?.product?.priceString ?? null,
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log("[gully-purchase-diag] pre-call diagnostic failed",
      { error: String((e && e.message) || e) });
  }
  try {
    const res = await sdk.Purchases.purchasePackage({ aPackage: pkg });
    const info = res?.customerInfo;
    _lastCustomerInfo   = info || _lastCustomerInfo;
    _lastCustomerInfoAt = Date.now();
    const plusActive = _entitlementIsActive(info);
    let _active_keys = [];
    let _all_entitlement_keys = [];
    try {
      _active_keys = Object.keys((info && info.entitlements && info.entitlements.active) || {});
    } catch { _active_keys = []; }
    try {
      _all_entitlement_keys = Object.keys((info && info.entitlements && info.entitlements.all) || {});
    } catch { _all_entitlement_keys = []; }
    // eslint-disable-next-line no-console
    console.log("[gully-purchase-diag] Purchases.purchasePackage resolved", {
      plusActive,
      has_customerInfo: !!info,
      entitlements_active_keys: _active_keys,
      entitlements_all_keys: _all_entitlement_keys,
    });
    // XX.240e — AWAIT the resolved post so WKWebView cannot drop it.
    // Distinguish "resolved_success" (entitlement active) from
    // "resolved_but_no_entitlement" (StoreKit charged, RC returned
    // customerInfo, but the mapped entitlement was NOT active — the
    // exact failure that surfaces as "Couldn't complete that purchase"
    // in the UI while Apple actually processed the sale).
    await _postPurchaseDiag({
      phase: plusActive ? "resolved_success" : "resolved_but_no_entitlement",
      customer_info_returned: !!info,
      active_entitlements:    _active_keys,
      package_identifier:     pkg?.identifier ?? null,
      product_identifier:     pkg?.product?.identifier ?? null,
      offering_identifier:    pkg?.offeringIdentifier ?? null,
      extra: {
        entitlements_all_keys:            _all_entitlement_keys,
        original_app_user_id:             info?.originalAppUserId ?? null,
        original_purchase_date:           info?.originalPurchaseDate ?? null,
        latest_expiration_date:           info?.latestExpirationDate ?? null,
        active_subscriptions:             Array.isArray(info?.activeSubscriptions) ? info.activeSubscriptions : null,
        all_purchased_product_identifiers: Array.isArray(info?.allPurchasedProductIdentifiers) ? info.allPurchasedProductIdentifiers : null,
        management_url:                   info?.managementURL ?? null,
      },
    });
    return { ok: true, plusActive, customerInfo: info };
  } catch (e) {
    // Iter XX.229 — TEMPORARY diagnostic: FULL RC + StoreKit error
    // capture. RevenueCat 11.x surfaces `errorCode`, `underlyingErrorMessage`,
    // `readableErrorCode`, `userCancelled`, plus a `code` string on iOS.
    try {
      // eslint-disable-next-line no-console
      console.log("[gully-purchase-diag] Purchases.purchasePackage threw", {
        userCancelled:           e?.userCancelled === true,
        errorCode:               e?.errorCode ?? null,
        readableErrorCode:       e?.readableErrorCode ?? null,
        code:                    e?.code ?? null,
        message:                 String((e && e.message) || e),
        localizedDescription:    e?.localizedDescription ?? null,
        underlyingErrorMessage:  e?.underlyingErrorMessage ?? null,
        underlyingErrorCode:     e?.underlyingErrorCode ?? null,
        domain:                  e?.domain ?? null,
        name:                    (e && e.name) || null,
        stack_head:              String(((e && e.stack) || "")).split("\n").slice(0, 3).join(" | "),
        // Best-effort JSON snapshot for anything we didn't name above.
        raw_keys:                (() => { try { return Object.keys(e || {}); } catch { return "err"; } })(),
      });
    } catch { /* noop */ }
    // Persist to backend — the single most important diagnostic since
    // Xcode + Safari consoles were both empty on last device test.
    const _wasCancelled = !!(e && (e.userCancelled === true || e?.errorCode === 1));
    const _isPending    = e?.errorCode === 15 || /pending/i.test(String(e?.message || ""));
    const _postPurchaseDiag_final = _postPurchaseDiag({
      phase:                  _wasCancelled ? "cancelled" : (_isPending ? "pending" : "error"),
      user_cancelled:         _wasCancelled,
      payment_pending:        _isPending,
      readable_error_code:    e?.readableErrorCode ?? null,
      underlying_error_code:  e?.underlyingErrorCode != null
                                ? String(e.underlyingErrorCode)
                                : (e?.underlyingErrorMessage ?? null),
      error_message:          String((e && e.message) || e),
      error_domain:           e?.domain ?? null,
      error_keys:             _errorKeys(e),
      package_identifier:     pkg?.identifier ?? null,
      product_identifier:     pkg?.product?.identifier ?? null,
      offering_identifier:    pkg?.offeringIdentifier ?? null,
      extra: {
        errorCode:            e?.errorCode ?? null,
        code:                 e?.code ?? null,
        localizedDescription: e?.localizedDescription ?? null,
        underlyingErrorMessage: e?.underlyingErrorMessage ?? null,
        name:                 (e && e.name) || null,
      },
    });
    // XX.240e — AWAIT the post so WKWebView cannot drop it after
    // the native StoreKit sheet dismisses and we early-return below.
    try { await _postPurchaseDiag_final; } catch { /* noop */ }
    // Iter XX.283 — Normalize the RC / Play / StoreKit error into a
    // stable low-cardinality category BEFORE branching on cancel /
    // pending. Attached to every non-success return so downstream
    // funnel events (`plus_purchase_failed_native` +
    // `checkout_failed`) can slice failure modes without ambiguity.
    const _errCategory = _normalizeRcErrorCategory(e);
    // Surface the raw signals we already log to `_postPurchaseDiag`
    // in a compact object so the failure event has enough context to
    // audit without pulling the diag store.
    const _errDetails = {
      readable_error_code:   e?.readableErrorCode ?? null,
      error_code:            (typeof e?.errorCode === "number") ? e.errorCode : null,
      underlying_error_code: e?.underlyingErrorCode != null
                               ? String(e.underlyingErrorCode) : null,
      user_cancelled:        e?.userCancelled === true,
      // Trim message aggressively — never surface stack traces or
      // raw StoreKit customer-facing strings via analytics.
      message:               String((e && e.message) || "").slice(0, 160),
    };
    // RC throws with `userCancelled` on Play sheet dismiss.
    if (_wasCancelled) {
      return { ok: false, reason: "user_cancelled",
               error_category: _errCategory, error_details: _errDetails };
    }
    // Play returns `PENDING` for cash / gift-card style purchases.
    if (_isPending) {
      return { ok: false, reason: "pending",
               error_category: _errCategory, error_details: _errDetails };
    }
    return { ok: false, reason: "error",
             error_category: _errCategory,
             error_details: _errDetails,
             error: String(e?.message || e) };
  }
}

/**
 * Restore Purchases — user-initiated only, per Play + RC guidance.
 * Never called automatically from a boot path.
 */
export async function restorePurchases() {
  if (!isRevenueCatReady()) return { ok: false, reason: "rc_not_ready" };
  const sdk = await _loadSdk();
  try {
    const info = await sdk.Purchases.restorePurchases();
    // v12 shape: { customerInfo }; older builds return CustomerInfo directly.
    const ci = info?.customerInfo || info;
    _lastCustomerInfo   = ci || _lastCustomerInfo;
    _lastCustomerInfoAt = Date.now();
    const plusActive = _entitlementIsActive(ci);
    return { ok: true, plusActive, customerInfo: ci };
  } catch (e) {
    return { ok: false, reason: "error", error: String(e?.message || e) };
  }
}

/** Force-fetch the latest CustomerInfo. Safe on app resume. */
export async function refreshCustomerInfo() {
  if (!isRevenueCatReady()) return { ok: false, reason: "rc_not_ready" };
  const sdk = await _loadSdk();
  try {
    const info = await sdk.Purchases.getCustomerInfo();
    const ci = info?.customerInfo || info;
    _lastCustomerInfo   = ci || _lastCustomerInfo;
    _lastCustomerInfoAt = Date.now();
    return { ok: true, customerInfo: ci, plusActive: _entitlementIsActive(ci) };
  } catch (e) {
    return { ok: false, reason: "error", error: String(e?.message || e) };
  }
}

/** Subscribe to CustomerInfo updates. Returns an unsubscribe fn. */
export function onCustomerInfoUpdated(cb) {
  if (typeof cb !== "function") return () => {};
  _listeners.add(cb);
  // Fire immediately with the last snapshot if we have one.
  if (_lastCustomerInfo) {
    try { cb(_lastCustomerInfo); } catch { /* isolate */ }
  }
  return () => { _listeners.delete(cb); };
}

/** True if the `plus` entitlement is active in the given CustomerInfo. */
function _entitlementIsActive(customerInfo) {
  try {
    const ents = customerInfo?.entitlements?.active || {};
    // Founder decision (Feb 2026) — the entitlement identifier is `plus`.
    return Boolean(ents.plus);
  } catch { return false; }
}

/**
 * TEST-ONLY export — lets unit tests reset the module singleton
 * between assertions. NEVER call from production code.
 */
export function __resetForTests() {
  _configured        = false;
  _configuring       = false;
  _lastCustomerInfo  = null;
  _lastCustomerInfoAt = 0;
  // Iter XX.295 — Reset the availability state machine + the
  // once-per-session configure-result report flag.
  _availabilitySnapshot = { state: "initializing", reason: "rc_configuring" };
  _availabilityListeners.clear();
  _configureResultReported = false;
  // Iter XX.230 — Clear the last-good-package cache on reset so
  // tests never leak state between assertions.
  _lastGoodMonthlyPackage = null;
  _listeners.clear();
  // Iter XX.231 — TEMPORARY diagnostic: prove the cache was cleared
  // during a test reset (should NOT appear on a real device — if it
  // does, a test harness is running in production).
  // eslint-disable-next-line no-console
  console.log("[gully-purchase-diag] cache cleared", { where: "__resetForTests" });
}

/**
 * Iter XX.230 — Production-safe cache reset.
 *
 * Called from the auth-provider `logout()` flow. Does NOT touch
 * RevenueCat SDK state (Purchases.logOut, entitlement cache, listener
 * registrations) — those are RC's responsibility and out of scope.
 * Only nulls the JS-side last-known-good monthly package so a new
 * player's session cannot receive the previous user's cached offering.
 */
export function clearMonthlyPackageCacheOnLogout() {
  _lastGoodMonthlyPackage = null;
  // Iter XX.231 — TEMPORARY diagnostic: prove the cache was cleared
  // during a real production logout (the ONLY legitimate reason for
  // the cache to be empty between Plus-page mount and purchase tap).
  // eslint-disable-next-line no-console
  console.log("[gully-purchase-diag] cache cleared", { where: "clearMonthlyPackageCacheOnLogout" });
}
